import React, { useState, useEffect } from 'react'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
  DocumentService,
  getStatusColor,
  getPriorityColor,
  formatDate,
  formatFileSize,
} from '@/services/documentService'
import { Document } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FileText, Eye, Trash2, CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react'

interface DocumentListProps {
  onDocumentSelect?: (document: Document) => void
  showActions?: boolean
}

export const DocumentList: React.FC<DocumentListProps> = ({
  onDocumentSelect,
  showActions = true,
}) => {
  const { branding } = useBranding()
  const pageStyles = getPageStyles(branding)
  const cardStyles = getCardStyles(branding)
  const statusColors = getStatusColors(branding)
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState({
    status: 'all',
    priority: 'all',
    search: '',
  })

  useEffect(() => {
    loadDocuments()
  }, [])

  const loadDocuments = async () => {
    try {
      setLoading(true)
      const data = await DocumentService.getDocuments()
      // Ensure data is always an array
      setDocuments(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load documents')
      // Set empty array on error to prevent filter issues
      setDocuments([])
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this document?')) {
      return
    }

    try {
      await DocumentService.deleteDocument(id)
      setDocuments((Array.isArray(documents) ? documents : []).filter(doc => doc.id !== id))
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete document')
    }
  }

  const filteredDocuments = (Array.isArray(documents) ? documents : []).filter(doc => {
    const matchesStatus = filter.status === 'all' || doc.status === filter.status
    const matchesPriority = filter.priority === 'all' || doc.priority === filter.priority
    const matchesSearch =
      !filter.search ||
      doc.document_type_display.toLowerCase().includes(filter.search.toLowerCase()) ||
      doc.original_filename?.toLowerCase().includes(filter.search.toLowerCase())

    return matchesStatus && matchesPriority && matchesSearch
  })

  if (loading) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: `${branding.primaryColor} transparent transparent transparent` }}></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen" style={pageStyles.container}>
        <div className="relative z-10 p-6">
          <div className="p-6 mb-8 transition-all duration-300 border card backdrop-blur-sm" style={{ ...cardStyles, borderColor: statusColors.error.borderColor }}>
            <div className="flex items-start space-x-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                <XCircle className="w-6 h-6" style={{ color: statusColors.error.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="mb-1 text-lg font-semibold" style={pageStyles.text}>Error loading documents</h3>
                <p className="mb-4 text-sm" style={{ color: statusColors.error.color }}>{error}</p>
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadDocuments}
                    className="hover:opacity-80"
                    style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor, color: branding.textColor }}
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setError(null)}
                    className="hover:opacity-80"
                    style={{ color: statusColors.error.color }}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={pageStyles.container}>
      <div className="relative z-10 p-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-2 lg:grid-cols-4 sm:gap-6 sm:mb-8">
          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Total Documents
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {documents.length.toLocaleString()}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>All documents</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: branding.primaryColor }}>
                  <FileText className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Approved
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {documents.filter(d => d.status === 'APPROVED').length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Successfully verified</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.success.backgroundColor }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Pending
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {documents.filter(d => d.status === 'PENDING').length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Awaiting review</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.warning.backgroundColor }}>
                  <Clock className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>

          <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
            <div className="p-6 card-content">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-semibold tracking-wide uppercase" style={pageStyles.textSecondary}>
                    Rejected
                  </p>
                  <p className="mt-2 text-3xl font-bold" style={pageStyles.text}>
                    {documents.filter(d => d.status === 'REJECTED').length}
                  </p>
                  <p className="mt-1 text-xs" style={pageStyles.textSecondary}>Need attention</p>
                </div>
                <div className="p-4 shadow-lg rounded-2xl" style={{ backgroundColor: statusColors.error.backgroundColor }}>
                  <XCircle className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm mb-6" style={{ ...cardStyles }}>
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4" style={pageStyles.text}>Filter Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold mb-2" style={pageStyles.textSecondary}>Search</label>
                <Input
                  placeholder="Search documents..."
                  value={filter.search}
                  onChange={e => setFilter({ ...filter, search: e.target.value })}
                  className="hover:opacity-80"
                  style={{
                    backgroundColor: `${branding.surfaceColor}80`,
                    borderColor: `${branding.textSecondaryColor}30`,
                    color: branding.textColor
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={pageStyles.textSecondary}>Status</label>
                <Select
                  value={filter.status}
                  onValueChange={value => setFilter({ ...filter, status: value })}
                >
                  <SelectTrigger className="hover:opacity-80" style={{
                    backgroundColor: `${branding.surfaceColor}80`,
                    borderColor: `${branding.textSecondaryColor}30`,
                    color: branding.textColor
                  }}>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent className="hover:opacity-80" style={{
                    backgroundColor: `${branding.surfaceColor}90`,
                    borderColor: `${branding.textSecondaryColor}30`
                  }}>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="PENDING">Pending</SelectItem>
                    <SelectItem value="APPROVED">Approved</SelectItem>
                    <SelectItem value="REJECTED">Rejected</SelectItem>
                    <SelectItem value="EXPIRED">Expired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2" style={pageStyles.textSecondary}>Priority</label>
                <Select
                  value={filter.priority}
                  onValueChange={value => setFilter({ ...filter, priority: value })}
                >
                  <SelectTrigger className="hover:opacity-80" style={{
                    backgroundColor: `${branding.surfaceColor}80`,
                    borderColor: `${branding.textSecondaryColor}30`,
                    color: branding.textColor
                  }}>
                    <SelectValue placeholder="All Priority" />
                  </SelectTrigger>
                  <SelectContent className="hover:opacity-80" style={{
                    backgroundColor: `${branding.surfaceColor}90`,
                    borderColor: `${branding.textSecondaryColor}30`
                  }}>
                    <SelectItem value="all">All Priority</SelectItem>
                    <SelectItem value="REQUIRED">Required</SelectItem>
                    <SelectItem value="OPTIONAL">Optional</SelectItem>
                    <SelectItem value="RECOMMENDED">Recommended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Documents List */}
        <div className="transition-all duration-300 border card hover:shadow-xl backdrop-blur-sm" style={{ ...cardStyles }}>
          <div className="p-6">
            {filteredDocuments.length === 0 ? (
              <div className="p-12 text-center">
                <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full mb-4" style={{ backgroundColor: `${branding.surfaceColor}30` }}>
                  <FileText className="w-8 h-8" style={{ color: branding.textSecondaryColor }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={pageStyles.text}>No documents found</h3>
                <p style={pageStyles.textSecondary}>Upload your first document to get started</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredDocuments.map(document => (
                  <div
                    key={document.id}
                    className="p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] rounded-lg border"
                    style={{ backgroundColor: `${branding.surfaceColor}40`, borderColor: `${branding.textSecondaryColor}20` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4">
                          <div>
                            <h4 className="font-medium" style={pageStyles.text}>
                              {document.document_type_display}
                            </h4>
                            <p className="text-sm" style={pageStyles.textSecondary}>
                              {document.original_filename || 'No filename'}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                                document.status
                              )}`}
                            >
                              {document.status_display}
                            </span>
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(
                                document.priority
                              )}`}
                            >
                              {document.priority_display}
                            </span>
                          </div>
                        </div>

                        <div className="mt-2 flex items-center space-x-4 text-sm" style={pageStyles.textSecondary}>
                          <span>Size: {formatFileSize(document.file_size)}</span>
                          <span>Uploaded: {formatDate(document.uploaded_at)}</span>
                          {document.reviewed_at && (
                            <span>Reviewed: {formatDate(document.reviewed_at)}</span>
                          )}
                          {document.days_until_expiry !== null && (
                            <span>Expires in: {document.days_until_expiry} days</span>
                          )}
                        </div>

                        {document.review_notes && (
                          <div className="mt-2 p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg text-sm">
                            <strong className="text-blue-200">Review Notes:</strong>
                            <span className="text-blue-100 ml-1">{document.review_notes}</span>
                          </div>
                        )}

                        {document.rejection_reason && (
                          <div className="mt-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-sm">
                            <strong className="text-red-200">Rejection Reason:</strong>
                            <span className="text-red-100 ml-1">{document.rejection_reason}</span>
                          </div>
                        )}
                      </div>

                      {showActions && (
                        <div className="flex space-x-3">
                          {document.status === 'PENDING' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(document.id)}
                              className="hover:opacity-80"
                              style={{ backgroundColor: statusColors.error.backgroundColor, borderColor: statusColors.error.borderColor, color: branding.textColor }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(document.file, '_blank')}
                            className="hover:opacity-80"
                            style={{ backgroundColor: statusColors.info.backgroundColor, borderColor: statusColors.info.borderColor, color: branding.textColor }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View
                          </Button>
                          {onDocumentSelect && (
                            <Button
                              size="sm"
                              onClick={() => onDocumentSelect(document)}
                              className="hover:opacity-80"
                              style={pageStyles.primaryBg}
                            >
                              Select
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
